import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState, useRef } from 'react'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { hapticLight } from '@/lib/native'

interface FloatingWalletProps {
  active: boolean
  startValue: number
  endValue: number
  onComplete?: () => void
  isTopBarVisible: boolean
}

export function FloatingWallet({ 
  active, 
  startValue, 
  endValue, 
  onComplete,
  isTopBarVisible 
}: FloatingWalletProps) {
  // Ensure values are valid numbers (defensive against NaN)
  const safeStartValue = typeof startValue === 'number' && !isNaN(startValue) && isFinite(startValue) ? startValue : 0
  const safeEndValue = typeof endValue === 'number' && !isNaN(endValue) && isFinite(endValue) ? endValue : safeStartValue
  
  const [displayValue, setDisplayValue] = useState(safeStartValue)
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldShow, setShouldShow] = useState(false)
  const animationRef = useRef<number | null>(null)
  const completedRef = useRef(false)
  
  // Start animation when active and TopBar is not visible
  useEffect(() => {
    if (active && !isTopBarVisible) {
      setShouldShow(true)
      setIsAnimating(true)
      completedRef.current = false
      setDisplayValue(safeStartValue)
      
      // Count up animation
      const difference = safeEndValue - safeStartValue
      
      // Skip animation if no change or invalid difference
      if (difference <= 0 || !isFinite(difference)) {
        setDisplayValue(safeEndValue)
        setIsAnimating(false)
        completedRef.current = true
        animationRef.current = window.setTimeout(() => {
          onComplete?.()
        }, 800)
        return
      }
      
      const duration = 1000 // 1 second for count up
      const steps = Math.min(difference, 15) // Max 15 steps for smooth animation
      const stepDuration = duration / steps
      
      let currentStep = 0
      
      const animate = () => {
        currentStep++
        const progress = currentStep / steps
        // Ease out curve for smoother end
        const easedProgress = 1 - Math.pow(1 - progress, 3)
        const newValue = Math.round(safeStartValue + (difference * easedProgress))
        
        setDisplayValue(newValue)
        
        // Haptic on each number change
        if (currentStep % 3 === 0 || currentStep === steps) {
          hapticLight()
        }
        
        if (currentStep < steps) {
          animationRef.current = window.setTimeout(animate, stepDuration)
        } else {
          // Animation complete
          setIsAnimating(false)
          completedRef.current = true
          
          // Wait a brief moment, then check if we should dismiss
          animationRef.current = window.setTimeout(() => {
            onComplete?.()
          }, 800)
        }
      }
      
      // Start with a small delay
      animationRef.current = window.setTimeout(animate, 200)
    }
    
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current)
      }
    }
  }, [active, isTopBarVisible, safeStartValue, safeEndValue, onComplete])
  
  // Hide when TopBar becomes visible (user scrolled to top)
  useEffect(() => {
    if (isTopBarVisible && shouldShow && !isAnimating) {
      setShouldShow(false)
    }
  }, [isTopBarVisible, shouldShow, isAnimating])
  
  // Reset when not active
  useEffect(() => {
    if (!active) {
      setShouldShow(false)
      setIsAnimating(false)
      completedRef.current = false
    }
  }, [active])
  
  const amount = safeEndValue - safeStartValue
  
  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: -80, opacity: 0, scale: 0.8 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -60, opacity: 0, scale: 0.9 }}
          transition={{ 
            type: 'spring', 
            stiffness: 300, 
            damping: 25,
            mass: 0.8
          }}
          className="fixed z-[150] left-1/2 -translate-x-1/2"
          style={{ 
            top: 'calc(var(--native-safe-area-inset-top, env(safe-area-inset-top, 0px)) + 16px)'
          }}
        >
          <motion.div
            animate={isAnimating ? {
              scale: [1, 1.05, 1],
            } : {}}
            transition={{
              duration: 0.3,
              repeat: isAnimating ? Infinity : 0,
            }}
            className="relative"
          >
            {/* Main wallet pill */}
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-cyan-500/95 to-blue-500/95 backdrop-blur-lg shadow-2xl border border-white/20">
              {/* Gem icon with glow */}
              <motion.div
                animate={isAnimating ? {
                  rotate: [0, -10, 10, 0],
                  scale: [1, 1.2, 1],
                } : {}}
                transition={{
                  duration: 0.5,
                  repeat: isAnimating ? Infinity : 0,
                }}
              >
                <FluentEmoji emoji="💎" size={24} animated />
              </motion.div>
              
              {/* Count display */}
              <div className="flex items-baseline gap-1">
                <motion.span
                  key={displayValue}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="text-xl font-bold text-white tabular-nums min-w-[3ch] text-right"
                >
                  {displayValue}
                </motion.span>
              </div>
              
              {/* Amount earned indicator */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white/20"
              >
                <span className="text-sm font-semibold text-white/90">+{amount}</span>
              </motion.div>
            </div>
            
            {/* Glow effect */}
            <motion.div
              className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-400 blur-xl opacity-50"
              animate={isAnimating ? {
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1],
              } : { opacity: 0.3 }}
              transition={{
                duration: 0.8,
                repeat: isAnimating ? Infinity : 0,
              }}
            />
            
            {/* Sparkles during animation */}
            {isAnimating && (
              <>
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1.5 h-1.5 rounded-full bg-white"
                    initial={{ 
                      x: 0, 
                      y: 0, 
                      opacity: 0,
                      scale: 0 
                    }}
                    animate={{ 
                      x: (Math.random() - 0.5) * 80,
                      y: (Math.random() - 0.5) * 40 - 20,
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                    }}
                    transition={{
                      duration: 0.8,
                      delay: i * 0.15,
                      repeat: Infinity,
                      repeatDelay: 0.5,
                    }}
                    style={{
                      left: '50%',
                      top: '50%',
                    }}
                  />
                ))}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
